import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { deptStaffingApi, F106, F906 } from '../../../services/deptStaffingApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useDeptStaffingForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F106[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Master form fields
  const [deptCode, setDeptCode] = useState('');
  const [deptName, setDeptName] = useState('');
  const [category, setCategory] = useState(1);
  const [manager, setManager] = useState('');
  const [managerName, setManagerName] = useState('');
  const [extension, setExtension] = useState('');
  const [isOldCode, setIsOldCode] = useState(false);
  const [vicePresident, setVicePresident] = useState('');
  const [vpName, setVpName] = useState('');
  const [remark, setRemark] = useState('');
  const [deptGroup, setDeptGroup] = useState('');
  const [details, setDetails] = useState<F906[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Compute staffing totals from details
  const totalStaffing = details.reduce((sum, d) => sum + (d.f906_11 || 0), 0);
  const totalActual = details.reduce((sum, d) => sum + (d.f906_12 || 0), 0);

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await deptStaffingApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  const syncFromCurrent = useCallback((rec: F106) => {
    setDeptCode(rec.f106_01);
    setDeptName(rec.f106_11);
    setCategory(rec.f106_02);
    setManager(rec.f106_12);
    setManagerName(rec.f106_12a);
    setExtension(rec.f106_13);
    setIsOldCode(rec.f106_16);
    setVicePresident(rec.f106_17);
    setVpName(rec.f106_17a);
    setRemark(rec.f106_23);
    setDeptGroup(rec.f106_24);
    setDetails(rec.details || []);
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      syncFromCurrent(current);
    }
  }, [current, mode, syncFromCurrent]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setDeptCode('');
    setDeptName('');
    setCategory(1);
    setManager('');
    setManagerName('');
    setExtension('');
    setIsOldCode(false);
    setVicePresident('');
    setVpName('');
    setRemark('');
    setDeptGroup('');
    setDetails([]);
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    syncFromCurrent(current);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      syncFromCurrent(current);
    }
  };

  // Add detail row
  const addDetail = (titleCode: string) => {
    // Check duplicate
    if (details.some(d => d.f906_02 === titleCode)) {
      message.warning('此職稱已存在!');
      return;
    }
    setDetails([...details, {
      f906_01: deptCode,
      f906_02: titleCode,
      f906_11: 0,
      f906_12: 0,
      f906_91: '',
      f906_92: null,
      f906_93: '',
    }]);
  };

  // Remove detail row
  const removeDetail = (idx: number) => {
    setDetails(details.filter((_, i) => i !== idx));
  };

  // Update detail field
  const updateDetail = (idx: number, field: keyof F906, value: number | string) => {
    const newDetails = [...details];
    (newDetails[idx] as any)[field] = value;
    setDetails(newDetails);
  };

  // Save
  const save = async () => {
    try {
      const targetCode = deptCode;
      if (mode === 'add') {
        await deptStaffingApi.create({
          f106_01: deptCode,
          f106_02: category,
          f106_11: deptName,
          f106_12: manager,
          f106_12a: managerName,
          f106_13: extension,
          f106_16: isOldCode,
          f106_17: vicePresident,
          f106_17a: vpName,
          f106_23: remark,
          f106_24: deptGroup,
          details,
        });
        message.success('新增成功');
      } else {
        await deptStaffingApi.update(current!.f106_01, {
          f106_02: category,
          f106_11: deptName,
          f106_12: manager,
          f106_12a: managerName,
          f106_13: extension,
          f106_16: isOldCode,
          f106_17: vicePresident,
          f106_17a: vpName,
          f106_23: remark,
          f106_24: deptGroup,
          details,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const idx = data.findIndex(r => r.f106_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await deptStaffingApi.delete(current.f106_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f106_01 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    deptCode, setDeptCode,
    deptName, setDeptName,
    category, setCategory,
    manager, setManager,
    managerName, setManagerName,
    extension, setExtension,
    isOldCode, setIsOldCode,
    vicePresident, setVicePresident,
    vpName, setVpName,
    remark, setRemark,
    deptGroup, setDeptGroup,
    details, setDetails,
    totalStaffing, totalActual,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    addDetail, removeDetail, updateDetail,
    loadRecords,
  };
}
